import { useTracking } from '@folklore/tracking';
import { animated, useSpring } from '@react-spring/web';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock-upgrade';
import classNames from 'classnames';
import { useEffect, useRef, useState, ReactNode } from 'react';

import { MODAL_NAMESPACE } from '../../contexts/PopupsContext';
import PopupPortal from './PopupPortal';

import styles from '../../styles/popups/modal.module.css';

type ModalProps = {
    id: string;
    opened?: boolean;
    position?: 'top' | 'bottom' | 'center';
    withBackdrop?: boolean;
    withTransition?: boolean;
    withScrollLock?: boolean;
    className?: string;
    innerClassName?: string;
    children?: ReactNode;
    requestClose?: () => void;
    onClickBackdrop?: () => void;
    onOpened?: () => void;
    onClosed?: () => void;
};

function Modal({
    id,
    opened = true,
    position = 'center',
    withBackdrop = false,
    withTransition = false,
    withScrollLock = false,
    className = null,
    innerClassName = null,
    children = null,
    requestClose = null,
    onClickBackdrop = null,
    onOpened = null,
    onClosed = null,
}: ModalProps) {
    const [verticalPosition, horizontalPosition = verticalPosition] = position.split(' ');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const spring = useSpring({
        from: { y: opened ? '100%' : '0%', opacity: opened ? 0 : 1 },
        to: { y: opened ? '0%' : '100%', opacity: opened ? 1 : 0 },
        onStart: () => {
            setIsTransitioning(true);
        },
        onResolve: () => {
            if (!isTransitioning) {
                return;
            }
            setIsTransitioning(false);
            if (opened && withScrollLock) {
                disableBodyScroll(containerRef.current!);
            } else if (!opened && withScrollLock) {
                clearAllBodyScrollLocks();
            }
            if (opened && onOpened !== null) {
                onOpened();
            } else if (!opened && onClosed !== null) {
                onClosed();
            }
        },
    });

    useEffect(() => {
        if (withTransition) {
            return;
        }
        if (opened && withScrollLock) {
            disableBodyScroll(containerRef.current!);
        } else if (!opened && withScrollLock) {
            clearAllBodyScrollLocks();
        }
        if (opened && onOpened !== null) {
            onOpened();
        } else if (!opened && onClosed !== null) {
            onClosed();
        }
    }, [opened, onOpened, onClosed, withTransition]);

    useEffect(
        () => () => {
            if (withScrollLock) {
                clearAllBodyScrollLocks();
            }
        },
        [withScrollLock],
    );

    const tracking = useTracking();
    useEffect(() => {
        tracking.trackModal(id, opened ? 'open' : 'close');
    }, [id, opened]);

    return (
        <PopupPortal id={id} requestClose={requestClose} namespace={MODAL_NAMESPACE}>
            <div
                className={classNames([
                    styles.container,
                    {
                        [styles.isTransitioning]: withTransition && isTransitioning,
                        [styles[verticalPosition]]: verticalPosition !== null,
                        [styles[horizontalPosition]]: horizontalPosition !== null,
                    },
                    className,
                ])}
                data-nosnippet
                ref={containerRef}
            >
                <animated.div
                    style={withTransition ? spring : undefined}
                    className={classNames([styles.inner, innerClassName])}
                    tabIndex={-1}
                >
                    {children}
                </animated.div>
                {withBackdrop ? (
                    <animated.button
                        type="button"
                        className={styles.backdrop}
                        onClick={onClickBackdrop}
                        style={{
                            opacity: spring.opacity,
                        }}
                    />
                ) : null}
            </div>
        </PopupPortal>
    );
}

export default Modal;
